/**
 * Review carousel (snippets/reviews-carousel.liquid), used on the product
 * page and the homepage.
 */
(() => {
  /**
   * Reviews carousel. Judge.me's widget is hidden; once it has its reviews,
   * read each one (rating, name, date, text, photos) and build our own card,
   * so the layout doesn't depend on Judge.me's markup. The cards are added
   * twice so the row can loop forever: when it scrolls into the second set
   * it jumps back by one set. Moves on one card every few seconds, with
   * arrows either side, and pauses on hover, touch, focus or Read more.
   */
  const carousel = document.querySelector('[data-reviews-carousel]');
  const source = document.querySelector('[data-reviews-source]');
  if (carousel && source) {
    const HOLD = 3000;
    const autoplay = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const track = carousel.querySelector('[data-reviews-track]');
    const prevButton = carousel.querySelector('[data-reviews-prev]');
    const nextButton = carousel.querySelector('[data-reviews-next]');
    const { readMore, readLess, verified, ratingLabel } = carousel.dataset;
    const dateFormat = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    let cards = [];
    let timer = null;
    let paused = false;
    let visible = false;

    /** Pull the parts of one Judge.me review out of its markup */
    const readReview = (review) => {
      const ratingEl = review.querySelector('[data-score]');
      const rating = Number(ratingEl ? ratingEl.dataset.score : 5) || 5;
      const author = (review.querySelector('.jdgm-rev__author') || {}).textContent || '';
      const stamp = review.querySelector('.jdgm-rev__timestamp');
      const raw = stamp ? stamp.dataset.content || stamp.textContent : '';
      const parsed = raw ? new Date(raw.replace(' UTC', 'Z').replace(' ', 'T')) : null;
      const date = parsed && !Number.isNaN(parsed.getTime()) ? dateFormat.format(parsed) : raw.trim();
      const body = review.querySelector('.jdgm-rev__body');
      const paragraphs = body ? [...body.querySelectorAll('p')].map((p) => p.textContent.trim()) : [];
      const text = (paragraphs.length ? paragraphs.join('\n') : body ? body.textContent : '').trim();
      const isVerified =
        review.dataset.verifiedBuyer === 'true' || Boolean(review.querySelector('.jdgm-rev__buyer-badge'));
      // One photo per thumbnail: Judge.me links the full image and shows a
      // smaller copy, so take the link's URL and fall back to the image.
      const usable = (url) => (url && !url.startsWith('data:') ? url : null);
      const links = [...review.querySelectorAll('.jdgm-rev__pic-link')];
      const thumbs = links.length ? links : [...review.querySelectorAll('.jdgm-rev__pic-img')];
      const photos = thumbs
        .map((el) => {
          const img = el.tagName === 'IMG' ? el : el.querySelector('img');
          const href = el.tagName === 'A' ? usable(el.getAttribute('href')) || usable(el.dataset.mfpSrc) : null;
          return href || usable(img && img.dataset.src) || usable(img && img.getAttribute('src'));
        })
        .filter(Boolean);
      return { rating, author: author.trim(), date, text, isVerified, photos: [...new Set(photos)].slice(0, 3) };
    };

    const buildCard = (review) => {
      const card = document.createElement('article');
      card.className = 'review-card';
      card.setAttribute('role', 'listitem');

      const top = document.createElement('div');
      top.className = 'review-card__top';
      const stars = document.createElement('span');
      stars.className = 'review-card__stars';
      stars.style.setProperty('--rating', `${(review.rating / 5) * 100}%`);
      stars.textContent = '★★★★★';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', ratingLabel.replace('[rating]', review.rating));
      const date = document.createElement('span');
      date.className = 'review-card__date';
      date.textContent = review.date;
      top.append(stars, date);

      const author = document.createElement('p');
      author.className = 'review-card__author';
      author.textContent = review.author;
      if (review.isVerified) {
        const badge = document.createElement('span');
        badge.className = 'review-card__badge';
        badge.textContent = verified;
        author.append(badge);
      }

      const body = document.createElement('p');
      body.className = 'review-card__body';
      body.textContent = review.text;
      card.append(top, author, body);

      if (review.photos.length) {
        card.classList.add('review-card--photo');
        const pics = document.createElement('div');
        pics.className = 'review-card__pics';
        review.photos.forEach((url) => {
          const link = document.createElement('a');
          link.className = 'review-card__pic';
          link.href = url;
          link.target = '_blank';
          link.rel = 'noopener';
          const img = document.createElement('img');
          img.src = url;
          img.alt = '';
          img.loading = 'lazy';
          link.append(img);
          pics.append(link);
        });
        card.append(pics);
      }
      return card;
    };

    /** "Read more" only on cards whose text is cut short */
    const addReadMore = (card) => {
      const body = card.querySelector('.review-card__body');
      if (body.scrollHeight <= body.clientHeight + 1) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'review-card__more';
      button.textContent = readMore;
      button.setAttribute('aria-expanded', 'false');
      body.after(button);
    };

    const stepWidth = () => cards[1].offsetLeft - cards[0].offsetLeft;
    const setWidth = () => stepWidth() * cards.length;
    const wrap = () => {
      if (track.scrollLeft >= setWidth() - 1) {
        track.scrollTo({ left: track.scrollLeft - setWidth(), behavior: 'instant' });
      }
    };
    const go = (direction) => {
      wrap();
      let index = Math.round(track.scrollLeft / stepWidth());
      if (direction < 0 && index === 0) {
        index = cards.length;
        track.scrollTo({ left: index * stepWidth(), behavior: 'instant' });
      }
      track.scrollTo({ left: (index + direction) * stepWidth(), behavior: 'smooth' });
    };
    const schedule = () => {
      clearTimeout(timer);
      if (!autoplay || paused || !visible || document.hidden) return;
      timer = setTimeout(() => {
        go(1);
        schedule();
      }, HOLD);
    };
    const pause = () => {
      paused = true;
      clearTimeout(timer);
    };
    const resume = () => {
      if (track.querySelector('.is-expanded')) return;
      paused = false;
      schedule();
    };

    /**
     * Judge.me only renders the first page of reviews into the product page.
     * Fetch the remaining pages from the same endpoint its own page links
     * use, so every review joins the row. On any failure keep what we have.
     */
    const fetchRemainingReviews = async (first) => {
      const widget = source.querySelector('.jdgm-rev-widg');
      const total = Number(widget && widget.dataset.numberOfReviews) || 0;
      const perPage = first.length;
      if (!total || total <= perPage || !source.dataset.shopDomain) return [];
      const pages = Math.ceil(total / perPage);
      const requests = [];
      for (let page = 2; page <= pages; page += 1) {
        const params = new URLSearchParams({
          url: source.dataset.shopDomain,
          shop_domain: source.dataset.shopDomain,
          platform: 'shopify',
          page: String(page),
          per_page: String(perPage),
          product_id: source.dataset.id,
        });
        requests.push(
          fetch(`https://judge.me/reviews/reviews_for_widget?${params}`)
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
              if (!data || !data.html) return [];
              const doc = new DOMParser().parseFromString(data.html, 'text/html');
              return [...doc.querySelectorAll('.jdgm-rev')];
            })
            .catch(() => [])
        );
      }
      return (await Promise.all(requests)).flat();
    };

    let started = false;
    const start = async () => {
      const first = [...source.querySelectorAll('.jdgm-rev')];
      if (first.length === 0 || started) return started;
      started = true;

      const seen = new Set();
      const reviews = [...first, ...(await fetchRemainingReviews(first))]
        .filter((review) => {
          const id = review.dataset.reviewId;
          if (!id) return true;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .map(readReview)
        .filter((review) => review.text || review.photos.length);
      if (reviews.length === 0) return true;

      cards = reviews.map(buildCard);
      track.append(...cards);
      cards.forEach(addReadMore);
      if (cards.length < 2) return true;

      cards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((el) => (el.tabIndex = -1));
        track.append(clone);
      });

      prevButton.hidden = false;
      nextButton.hidden = false;
      prevButton.addEventListener('click', () => go(-1));
      nextButton.addEventListener('click', () => go(1));

      track.addEventListener('click', (event) => {
        const button = event.target.closest('.review-card__more');
        if (!button) return;
        const expanded = button.closest('.review-card').classList.toggle('is-expanded');
        button.textContent = expanded ? readLess : readMore;
        button.setAttribute('aria-expanded', String(expanded));
        if (expanded) pause();
        else resume();
      });

      let scrollEnd = null;
      track.addEventListener('scroll', () => {
        clearTimeout(scrollEnd);
        scrollEnd = setTimeout(wrap, 150);
      });

      carousel.addEventListener('pointerenter', pause);
      carousel.addEventListener('pointerleave', resume);
      carousel.addEventListener('focusin', pause);
      carousel.addEventListener('focusout', resume);
      track.addEventListener('touchstart', pause, { passive: true });
      track.addEventListener('touchend', () => setTimeout(resume, HOLD), { passive: true });
      new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        schedule();
      }).observe(track);
      document.addEventListener('visibilitychange', schedule);
      return true;
    };

    if (source.querySelector('.jdgm-rev')) {
      start();
    } else {
      const waitForReviews = new MutationObserver(() => {
        if (!source.querySelector('.jdgm-rev')) return;
        waitForReviews.disconnect();
        start();
      });
      waitForReviews.observe(source, { childList: true, subtree: true });
    }
  }
})();
